import { z } from "zod";
import type { AssetIdFormat } from "./asset-id";

// AssetId validation regex: <address>:<chainId>
// Matches either 0x...:chainId for EVM or base58(Solana):sol for Solana
const assetIdRegex =
  /^(0x[a-fA-F0-9]{40}:[0-9]+|[1-9A-HJ-NP-Za-km-z]{32,44}:[0-9]+)$/;

// Request/Response schemas for asset validation API
export const validateAssetsRequestSchema = z.object({
  assetIds: z
    .array(
      z
        .string()
        .regex(
          assetIdRegex,
          "Invalid AssetId format. Expected: <address>:<chainId>",
        ),
    )
    .min(1, "At least one asset required")
    .max(100, "Maximum 100 assets per request"),
});

export const validateAssetsResponseSchema = z.object({
  results: z.array(
    z.object({
      assetId: z.string(),
      isBlocked: z.boolean(),
    }),
  ),
});

// Types inferred from schemas
export type ValidateAssetsRequest = z.infer<typeof validateAssetsRequestSchema>;
export type ValidateAssetsResponse = z.infer<
  typeof validateAssetsResponseSchema
>;

// Interface for validation providers
export interface IAssetValidator {
  /**
   * Name of the validator (e.g., "GOPLUS", "MANUAL", "CHAINALYSIS")
   */
  name: string;

  /**
   * Validates a batch of tokens
   * @param tokens Array of tokens to validate
   * @returns Map where key is `${chainId}:${address.toLowerCase()}` and value is whether it's blocked
   */
  validate(assetIds: AssetIdFormat[]): Promise<Map<string, boolean>>;
}

// Cache entry structure
export interface BlocklistCacheEntry {
  isBlocked: boolean;
  sources: string[];
  checkedAt: number; // timestamp
}

// Configuration for cache TTLs
export const CACHE_TTL = {
  DB_FRESHNESS_BLOCKED: 86400 * 1000, // 24 hours in milliseconds
  DB_FRESHNESS_VALID: 2592000 * 1000, // 30 days in milliseconds
  L1_REDIS_BLOCKED: 86400, // 24 hours in seconds
  L1_REDIS_VALID: 2592000, // 30 days in seconds
} as const;

// Helper to generate cache key
export function getBlocklistCacheKey(chainId: number, address: string): string {
  return `blocklist:${chainId}:${address.toLowerCase()}`;
}
